<template>
  <div :class="['w-full', 'h-full', 'flex', 'items-center', 'justify-center', 'flex-col']">
    <div
      ref="embedWrapper"
      :class="{
        'overflow-auto': !isDocument && !isArticle,
        'overflow-hidden': isDocument || isArticle,
        'desktop:w-[768px]': isArticle && !disableArticleFixedWidth,
        'w-full': true,
        'h-full': !isTweet,
        'h-fit': isTweet,
        'flex justify-center': isHtml,
      }"
      v-html="embedHtml"
    />
  </div>
</template>

<script setup lang="ts">
import { useWindowSizeStore } from '@@/pinia/window_size'
import { observeSizeChange } from '@@/bits/resize_observer'
import { computed, nextTick, onMounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    embedCode: string
    isDocument: boolean
    isArticle: boolean
    isHtml: boolean
    isFacebookPost?: boolean
    isInstagramPost?: boolean
    isTweet?: boolean
    disableArticleFixedWidth?: boolean
  }>(),
  {
    isFacebookPost: false,
    isInstagramPost: false,
    isTweet: false,
    disableArticleFixedWidth: false,
  },
)

const emit = defineEmits<{
  (name: 'load'): void
}>()

const embedWrapper = ref()
const embedHtml = ref('')

const isSocialPost = computed(() => props.isFacebookPost || props.isInstagramPost || props.isTweet)

const setEmbedBackgroundColorToWhite = (embedTagIframe: HTMLIFrameElement) => {
  if (!props.isFacebookPost) return
  // Facebook embeds don't set background-color. In dark mode,
  // the background becomes black, hiding the text. We fix this
  // ourselves.
  embedTagIframe.style.setProperty('background-color', 'white')
}

const setDocumentEmbedSize = (embedTagIframe: HTMLIFrameElement): void => {
  if (!props.isDocument && !props.isArticle && !props.isHtml) return
  if (!isSocialPost.value) embedTagIframe.setAttribute('width', '100%')
  if (!props.isFacebookPost && !props.isTweet) embedTagIframe.setAttribute('height', '100%')
  if (props.isTweet) embedTagIframe.setAttribute('height', '0%') // Prevent flicker while waiting for setHeightForTweet to be called
}

const processEmbedCode = () => {
  const embedTag = embedWrapper.value.firstChild as Element
  if (embedTag.tagName.toLowerCase() === 'iframe') {
    const embedTagIframe = embedTag as HTMLIFrameElement
    setEmbedBackgroundColorToWhite(embedTagIframe)
    setDocumentEmbedSize(embedTagIframe)
    embedHtml.value = embedTagIframe.outerHTML
  }
}

const observeEmbedMutationForTweet = (embedTagIframe: HTMLIFrameElement) => {
  const observer = new MutationObserver((mutationList: MutationRecord[]) => {
    for (const mutation of mutationList) {
      mutation.addedNodes.forEach((node) => {
        // Need to check classList because other nodes can be added
        const isTwitterIframeAdded = (node as Element).classList.contains('twitter-tweet')
        if (isTwitterIframeAdded) {
          setHeightForTweet(embedTagIframe)
        }
      })
    }
  })
  observer.observe(embedTagIframe.contentDocument!, { childList: true, subtree: true })
}

const setHeightForTweet = (embedTagIframe: HTMLIFrameElement) => {
  const twitterIframe = embedTagIframe.contentDocument?.querySelector('iframe')
  observeSizeChange(twitterIframe!, (_: HTMLElement, contentRect: DOMRectReadOnly) => {
    if (contentRect.height === 0) {
      return
    }
    const IFRAME_VERTICAL_MARGIN = 20
    embedTagIframe.setAttribute('height', `${twitterIframe?.offsetHeight! + IFRAME_VERTICAL_MARGIN}px`)
    emit('load')
  })
}

const executeScriptElements = (containerElement: HTMLElement): void => {
  const scriptElements = containerElement.querySelectorAll('script')

  Array.from(scriptElements).forEach((scriptElement) => {
    const clonedElement = document.createElement('script')

    Array.from(scriptElement.attributes).forEach((attribute) => {
      clonedElement.setAttribute(attribute.name, attribute.value)
    })

    clonedElement.text = scriptElement.text

    scriptElement.parentNode?.replaceChild(clonedElement, scriptElement)
  })
}

onMounted(() => {
  embedHtml.value = props.embedCode
  nextTick(() => {
    processEmbedCode()
    executeScriptElements(embedWrapper.value)
    nextTick(() => {
      const embedTag = embedWrapper.value.firstChild as Element
      const isEmbedChildIframe = embedTag.tagName.toLowerCase() === 'iframe'
      if (!isEmbedChildIframe) {
        emit('load')
        return
      }

      const embedTagIframe = embedTag as HTMLIFrameElement
      embedTagIframe.addEventListener('load', (): void => {
        if (!props.isTweet) {
          emit('load')
          return
        }

        // There is no guarantee that `twitterIframe` exists even after `embedTagIframe` has fired `load`.
        // In the event that `twitterIframe` is null,
        // we can wait for `twitterIframe` to be added to the childList before we call `setHeightForTweet`.
        const twitterIframe = embedTagIframe.contentDocument?.querySelector('iframe')
        if (twitterIframe) {
          setHeightForTweet(embedTagIframe)
        } else {
          observeEmbedMutationForTweet(embedTagIframe)
        }
      })
    })
  })
})
</script>

<style lang="scss" scoped>
:deep(.embedly-card-hug) {
  background-color: white;
}
</style>
